import { useEffect, useRef } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import CashfreePaymentBridge from '../../components/CashfreePaymentBridge';
import {
  dismissNativeCashfreeModal,
  reconcileNativePaymentAfterResume,
} from '../services/nativeCashfree';

const LOG = '[V2Pay:resume]';

/**
 * When the user returns from PhonePe/GPay on iOS, Cashfree may not call verifyPayment.
 * Re-check payment on app resume (Capacitor + native plugin event) with retries.
 */
export function useNativePaymentResume({
  orderId,
  enabled,
  onPaymentSuccess,
  onPaymentFailed,
}) {
  const checking = useRef(false);
  const pollTimer = useRef(null);
  const orderIdRef = useRef(orderId);
  orderIdRef.current = orderId;

  const onSuccessRef = useRef(onPaymentSuccess);
  const onFailedRef = useRef(onPaymentFailed);
  onSuccessRef.current = onPaymentSuccess;
  onFailedRef.current = onPaymentFailed;

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !enabled || !orderId) {
      return undefined;
    }

    let appListener = null;
    let pluginListener = null;
    let cancelled = false;

    const checkOnResume = async (reason) => {
      const id = orderIdRef.current;
      if (!id || checking.current || cancelled) return;
      checking.current = true;
      console.info(LOG, 'reconcile_start', { reason, orderId: id });
      try {
        const txn = await reconcileNativePaymentAfterResume(id);
        console.info(LOG, 'reconcile_result', {
          status: txn.status,
          hasPilotOrderId: Boolean(txn.pilotOrderId),
        });
        if (txn.status === 'PAYMENT_SUCCESS') {
          await dismissNativeCashfreeModal({ resolveAsSuccess: true });
          onSuccessRef.current(txn, { reason: `resume_${reason}` });
        } else if (txn.status === 'PAYMENT_FAILED') {
          await dismissNativeCashfreeModal({ resolveAsSuccess: false });
          onFailedRef.current({ reason: `resume_${reason}` });
        }
      } catch (e) {
        console.warn(LOG, 'reconcile_error', e);
      } finally {
        checking.current = false;
      }
    };

    CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        checkOnResume('appStateChange');
      }
    }).then((handle) => {
      if (cancelled) {
        handle.remove();
      } else {
        appListener = handle;
      }
    });

    CashfreePaymentBridge.addListener('appResumed', (event) => {
      const eventOrderId = event?.orderId != null ? String(event.orderId) : '';
      if (eventOrderId && eventOrderId !== orderIdRef.current) return;
      checkOnResume('native_appResumed');
    }).then((handle) => {
      if (cancelled) {
        handle.remove();
      } else {
        pluginListener = handle;
      }
    });

    // Fallback poll while checkout is open (WKWebView may miss appStateChange).
    pollTimer.current = window.setInterval(() => {
      if (!document.hidden) {
        checkOnResume('interval');
      }
    }, 4000);

    return () => {
      cancelled = true;
      appListener?.remove();
      pluginListener?.remove();
      if (pollTimer.current) {
        window.clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [orderId, enabled]);
}
